import { ResetPassword } from './../_models/user';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Response, User } from '@app/_models';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from './base.service';
import { LoggerService } from './logger.service';

interface LoginResponse {
    user: User;
    token: string;
    tokenExpiration: Date;
}
@Injectable({
    providedIn: 'root'
})
export class AccountService {

    //Menu
    private userMenuEndpoint = "BackOfficeMcdsApi/Menu/List";
    private userDetailEndpoint = "BackOfficeMcdsApi/User/Detail";
    private pwRecoverEndpoint = "Account/ForgotPassword";
    private pwResetEndpoint = "Account/ResetPassword";
    private getDigitalFidelity ="BackOfficeMcdsApi/GetFidelityConnectedToApp"

    private userSubject: BehaviorSubject<User | null>;
    public user: Observable<User | null>;

    private menuSubject: BehaviorSubject<string[]>;
    public menu: Observable<string[]>;

    constructor(
        private router: Router,
        private http: HttpClient,
        private baseService: BaseService,
        private logger: LoggerService
    ) {
        const localStoredUser = localStorage.getItem('user') || '';
        const initialUser = localStoredUser !== '' ? JSON.parse(localStoredUser) : null;

        // Initialize BehaviorSubject with initial value (no subscription needed!)
        this.userSubject = new BehaviorSubject<User | null>(initialUser);
        this.user = this.userSubject.asObservable();

        // Initialize menu BehaviorSubject
        this.menuSubject = new BehaviorSubject<string[]>([]);
        this.menu = this.menuSubject.asObservable();
    }

    // Getter for synchronous access to current user (no subscription needed!)
    public get userValue(): User | null {
        return this.userSubject.value;
    }

    // Getter for synchronous access to user menu
    public get userMenu(): string[] {
        return this.menuSubject.value;
    }

    login(username: string, password: string) {
        const authJson = {
            username: username,
            password: password
        };
        const authString = JSON.stringify(authJson);
        return this.http.post<Response<LoginResponse>>(`${environment.apiURL}${environment.apiVersion}Administration/Login`, authJson)
            .pipe(map(response => {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                if (response.success && response.content) {
                    this.logger.log("Auth success");
                    const user = response.content.user;
                    const storageUser = new User(user.id, user.email, user.username, user.phone_number, user.roles, response.content.token);
                    localStorage.setItem('user', JSON.stringify(storageUser));
                    this.userSubject.next(storageUser);
                    this.refreshUserMenu().subscribe({
                        error: err => this.logger.error("Failed to refresh menu after login", err)
                    });
                }
                else {
                    this.logger.log("Auth failure");
                    throw new Error(response.error)
                }
                return response;

            }));
    }

    logout() {
        // remove user from local storage and set current user to null
        localStorage.removeItem('user');
        this.userSubject.next(null!);
        this.router.navigate(['/account/login']);
    }

    pwRecover(email: string) {
        const recoverJson = {
            email: email,
            urlBase: location.origin
        };
        return this.baseService.postRequest<any>(recoverJson, this.pwRecoverEndpoint, (data: any) => {
            this.logger.log("Sent pw reset request")
        });
    }

    pwReset(resetPassword: ResetPassword) {
        return this.baseService.postRequest<any>(resetPassword, this.pwResetEndpoint, (data: any) => {
            this.logger.log("Pw reset request")
        });
    }

    register(user: User) {
        return this.http.post(`${environment.apiURL}/users/register`, user);
    }

    getAll() {
        return this.http.get<User[]>(`${environment.apiURL}/users`);
    }

    getById(id: string) {
        return this.http.get<User>(`${environment.apiURL}/users/${id}`);
    }

    // TODO: if used, point to backend API to store in DB ar whatever will be used in application
    update(id: string, params: any) {
        return this.http.put(`${environment.apiURL}/users/${id}`, params)
            .pipe(map(x => {
                // update stored user if the logged in user updated their own record
                if (id == this.userValue!.id) {
                    // update local storage
                    const user = { ...this.userValue, ...params };
                    localStorage.setItem('user', JSON.stringify(user));

                    // publish updated user to subscribers
                    this.userSubject.next(user);
                }
                return x;
            }));
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiURL}/users/${id}`)
            .pipe(map(x => {
                // auto logout if the logged in user deleted their own record
                if (id == this.userValue!.id) {
                    this.logout();
                }
                return x;
            }));
    }

    public refreshUserMenu() {
        return this.baseService.getOnce<string[]>(this.userMenuEndpoint, (data: string[]) => {
            this.menuSubject.next(data);
        }); 
    }

    public getFidelityWithApp() {
        return this.baseService.getOnce<string[]>(this.getDigitalFidelity, (data: string[]) => {
            this.menuSubject.next(data);
        }); 
    }
}
